/**
 * Utility functions for calculating tutor status based on verification status and live status
 */

export interface TutorStatusResult {
  status: string;
  displayValue: string;
}

/**
 * Calculate the final status of a tutor based on verification_status and is_live
 * @param verification_status - The verification status of the tutor
 * @param is_live - Whether the tutor is live/active
 * @returns Object containing the status and display value
 */
export const calculateTutorStatus = (
  verification_status: string | null,
  is_live: boolean | null = false
): TutorStatusResult => {
  // Handle null or undefined verification_status
  if (!verification_status) {
    return {
      status: 'unknown',
      displayValue: 'Unknown'
    };
  }

  switch (verification_status.toLowerCase()) {
    case 'submitted':
      return {
        status: 'submitted',
        displayValue: 'Submitted'
      };
    
    case 'under_review':
      return {
        status: 'under_review',
        displayValue: 'Under Review'
      };
    
    case 'rejected':
      return {
        status: 'rejected',
        displayValue: 'Rejected'
      };
    
    case 'approved':
      if (is_live === true) {
        return {
          status: 'active',
          displayValue: 'Active'
        };
      } else {
        return {
          status: 'approved',
          displayValue: 'Approved'
        };
      }
    
    default:
      return {
        status: 'unknown',
        displayValue: 'Unknown'
      };
  }
};

/**
 * Get the appropriate badge variant based on tutor status
 * @param status - The tutor status
 * @returns Badge variant string
 */
export const getTutorStatusBadgeVariant = (status: string): string => {
  switch (status) {
    case 'active':
      return 'default'; // Green
    case 'approved':
      return 'secondary'; // Blue
    case 'submitted':
      return 'outline'; // Gray outline
    case 'under_review':
      return 'secondary'; // Blue
    case 'rejected':
      return 'destructive'; // Red
    default:
      return 'outline'; // Gray outline
  }
};

/**
 * Get the appropriate CSS classes for tutor status badge
 * @param status - The tutor status
 * @returns CSS class string
 */
export const getTutorStatusBadgeClasses = (status: string): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'approved':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'submitted':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'under_review':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};